/**
 * @file Frontend API client — reliabilityStandardsService.
 */

import axiosClient from "../api/axiosClient";

/** Type definition — Reliability Standard Config. */
export interface ReliabilityStandardConfig {
  id: number;
  dataReviewValue: string;
  dataCompletenessValue: string;
  dataAcquisitionValue: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Type definition — Reliability Standard Response. */
export interface ReliabilityStandardResponse {
  success: boolean;
  data?: ReliabilityStandardConfig;
  message?: string;
}

/** Type definition — Create Reliability Standard Payload. */
export interface CreateReliabilityStandardPayload {
  dataReviewValue: string;
  dataCompletenessValue: string;
  dataAcquisitionValue: string;
}

/** Type definition — Update Reliability Standard Payload. */
export interface UpdateReliabilityStandardPayload {
  dataReviewValue?: string;
  dataCompletenessValue?: string;
  dataAcquisitionValue?: string;
  isActive?: boolean;
}

/** Exported const — reliability Standards Service. */
export const reliabilityStandardsService = {
  /**
   * Get the currently active reliability standard configuration
   */
  getActive: async (): Promise<ReliabilityStandardResponse> => {
    try {
      const response = await axiosClient.get("/reliability-standards");
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: error.response?.data?.message || "No active reliability standard configuration found",
        };
      }
      throw error;
    }
  },

  /**
   * Create a new reliability standard configuration (Admin only)
   */
  create: async (
    payload: CreateReliabilityStandardPayload
  ): Promise<ReliabilityStandardResponse> => {
    try {
      const response = await axiosClient.post("/reliability-standards", payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        return {
          success: false,
          message: error.response?.data?.message || "All reliability standard values are required",
        };
      }
      throw error;
    }
  },

  /**
   * Update an existing reliability standard configuration (Admin only)
   */
  update: async (
    id: number,
    payload: UpdateReliabilityStandardPayload
  ): Promise<ReliabilityStandardResponse> => {
    try {
      const response = await axiosClient.put(`/reliability-standards/${id}`, payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: error.response?.data?.message || "Reliability standard configuration not found",
        };
      }
      throw error;
    }
  },
};

